import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMPTY, Subject, Subscription } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { EvaService } from "src/app/core";
import { AttachmentSearchGroup, Note, NoteAttachment, NoteContext } from "src/app/domains/note";
import { NoteService } from "src/app/services/note.service";
import { EmployeesService } from "src/app/features/employees/services/employees.service";
import { AssigneeItem } from "src/app/features/task/task-assignee-picker/task-assignee-picker.component";
import { EVA_OBJECT_TAGS, type TagPreset } from "src/app/shared/constants/tag-presets";

/** Mapping backend object_type → clé EVA_OBJECT_TAGS */
const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  OFFER: "OFFRE",
  INVOICE: "FACTURE_CLIENT",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  INTERVENTION: "INTERVENTION",
  CONTRACT: "CONTRAT",
  CUSTOMER_SITE: "SITE",
  CATALOG: "CATALOGUE",
  ARTICLE: "ARTICLE",
  EXPENSE_REPORT: "NOTE_FRAIS",
  CONTACT: "CONTACT",
};

/** Mapping backend object_type → route prefix */
const OBJECT_TYPE_TO_ROUTE: Record<string, string> = {
  CUSTOMER: "customers",
  PROJECT: "projects",
  OFFER: "offers",
  INVOICE: "invoices/issued",
  EMPLOYEE: "employees",
  SUPPLIER: "suppliers",
  PURCHASE_ORDER: "purchase-orders",
  INTERVENTION: "interventions",
  CONTACT: "contacts",
  ARTICLE: "catalog/articles",
};
import { EvaMenuItem } from "src/app/shared/ds/eva-menu/eva-menu.component";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

type SaveStatus = "idle" | "saving" | "saved" | "error";

@Component({
  selector: "app-notes-detail",
  templateUrl: "./notes-detail.component.html",
  styleUrls: ["./notes-detail.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesDetailComponent implements OnChanges, OnDestroy {
  private destroyRef = inject(DestroyRef);

  // ==========================================================================
  // Inputs / Outputs
  // ==========================================================================

  /** ID de la note a charger */
  @Input() noteId: string | null = null;

  /** Contexte de rattachement (pour pre-remplir les attachments) */
  @Input() context: NoteContext | null = null;

  /** Emis pour revenir a la liste */
  @Output() backToList = new EventEmitter<void>();

  // ==========================================================================
  // State
  // ==========================================================================

  note: Note | null = null;
  isLoading: boolean = true;
  saveStatus: SaveStatus = "idle";

  /** Valeur unifiée de l'éditeur (titre = première ligne) */
  editorValue: string = "";

  /** Menu contextuel */
  contextMenuItems: EvaMenuItem[] = [
    {
      label: "Dupliquer",
      icon: "heroDocumentDuplicate",
      command: () => this.duplicateNote(),
    },
    {
      label: "Supprimer",
      icon: "heroTrash",
      severity: "danger",
      command: () => this.confirmDelete(),
    },
  ];

  /** Dialog de confirmation de suppression */
  showDeleteDialog = false;

  /** Recherche attachments */
  attachmentSearchQuery: string = "";
  attachmentSearchResults: AttachmentSearchGroup[] = [];
  isSearchingAttachments: boolean = false;

  /** Panneaux de recherche inline */
  showAttachmentSearch: boolean = false;

  /** Flag de modifications non sauvegardées */
  private isDirty: boolean = false;

  /** Valeur originale chargée du serveur (pour éviter les faux dirty) */
  private originalEditorValue: string = "";

  /** Debounce sujets */
  private autoSaveSubject = new Subject<void>();
  private attachmentSearchSubject = new Subject<string>();
  private autoSaveSubscription?: Subscription;
  private attachmentSearchSubscription?: Subscription;

  /** Max caractères (titre + contenu) */
  readonly MAX_CONTENT_LENGTH = 10200;

  /** Liste des employés pour le picker "Partager à" */
  employees: any[] = [];

  /** Collaborateurs avec qui la note est partagée */
  sharedWith: AssigneeItem[] = [];

  private employeesService = inject(EmployeesService);

  onDocumentClick(event: Event): void {
    if (this.showAttachmentSearch && !this.elementRef.nativeElement.contains(event.target)) {
      this.showAttachmentSearch = false;
      this.attachmentSearchQuery = "";
      this.attachmentSearchResults = [];
      this.cdr.markForCheck();
    }
  }

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showAttachmentSearch,
    (event) => this.onDocumentClick(event),
  );

  constructor(
    private noteService: NoteService,
    private evaService: EvaService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {
    this.setupAutoSave();
    this.setupAttachmentSearch();
    this.loadEmployees();
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["noteId"] && this.noteId) {
      this.loadNote(this.noteId);
    }
  }

  ngOnDestroy(): void {
    // Flush la sauvegarde en attente avant destruction
    this.flushSave();
    this.autoSaveSubscription?.unsubscribe();
    this.attachmentSearchSubscription?.unsubscribe();
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  get isPinned(): boolean {
    return this.note?.is_pinned || false;
  }

  get isPrivate(): boolean {
    return this.note?.is_private || false;
  }

  get isUrgent(): boolean {
    return this.note?.is_urgent || false;
  }

  /** Preset du tag affiché quand la note est épinglée */
  get pinnedTagPreset(): TagPreset {
    return { label: "Épinglé", severity: "warning", icon: "evaPin" };
  }

  get privateTagPreset(): TagPreset {
    return { label: "Privée", severity: "secondary", icon: "heroLockClosed" };
  }

  get urgentTagPreset(): TagPreset {
    return { label: "Urgent", severity: "danger", icon: "heroFlag" };
  }

  get createdInfo(): string {
    if (!this.note) return "";
    const name = this.note.created_by_name || "Inconnu";
    return `Créée par ${name} le ${this.formatDateShort(this.note.created_on)}`;
  }

  get updatedInfo(): string {
    if (!this.note) return "";
    return `Modifiée le ${this.formatDateFull(this.note.updated_on)}`;
  }

  get saveStatusLabel(): string {
    switch (this.saveStatus) {
      case "saving":
        return "Sauvegarde en cours...";
      case "saved":
        return "Sauvegardée";
      case "error":
        return "Erreur de sauvegarde";
      default:
        return "";
    }
  }

  // ==========================================================================
  // Data loading
  // ==========================================================================

  private loadNote(noteId: string): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.noteService
      .getById(noteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (note) => {
          this.note = note;
          this.editorValue = this.mergeToEditor(note.title, note.content);
          this.originalEditorValue = this.editorValue;
          this.isDirty = false;
          this.isLoading = false;
          this.saveStatus = "idle";
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.evaService.showError("Impossible de charger la note", "loadNote", "");
          this.cdr.markForCheck();
        },
      });
  }

  // ==========================================================================
  // Auto-save (debounce 2s)
  // ==========================================================================

  private setupAutoSave(): void {
    this.autoSaveSubscription = this.autoSaveSubject
      .pipe(
        debounceTime(2000),
        switchMap(() => this.performSave()),
      )
      .subscribe({
        next: (updatedNote) => {
          // Préserver les champs locaux non encore gérés par le backend
          this.note = {
            ...updatedNote,
            is_private: this.note?.is_private ?? updatedNote.is_private,
            is_urgent: this.note?.is_urgent ?? updatedNote.is_urgent,
          } as typeof this.note;
          this.isDirty = false;
          this.originalEditorValue = this.editorValue;
          this.saveStatus = "saved";
          this.cdr.markForCheck();

          // Reset status apres 3s
          setTimeout(() => {
            if (this.saveStatus === "saved") {
              this.saveStatus = "idle";
              this.cdr.markForCheck();
            }
          }, 3000);
        },
        error: () => {
          this.saveStatus = "error";
          this.evaService.showError("Erreur lors de la sauvegarde", "autoSave", "");
          this.cdr.markForCheck();
        },
      });
  }

  private performSave() {
    if (!this.note) return EMPTY;

    this.saveStatus = "saving";
    this.cdr.markForCheck();

    const { title, content } = this.splitFromEditor(this.editorValue);
    return this.noteService.update(this.note.note_id, {
      title,
      content,
      attachments: this.note.attachments,
      is_pinned: this.note.is_pinned,
      is_private: this.note.is_private,
      is_urgent: this.note.is_urgent,
    });
  }

  private triggerAutoSave(): void {
    this.autoSaveSubject.next();
  }

  /**
   * Sauvegarde immédiate (fire-and-forget) si des modifications sont en attente.
   * Utilisé avant navigation ou destruction du composant.
   */
  private flushSave(): void {
    if (!this.isDirty || !this.note) return;

    this.isDirty = false;
    const { title, content } = this.splitFromEditor(this.editorValue);
    this.noteService
      .update(this.note.note_id, {
        title,
        content,
        attachments: this.note.attachments,
        is_pinned: this.note.is_pinned,
        is_private: this.note.is_private,
        is_urgent: this.note.is_urgent,
      })
      .subscribe();
  }

  // ==========================================================================
  // Title & Content editing
  // ==========================================================================

  onEditorChange(value: string): void {
    this.editorValue = value;
    if (value === this.originalEditorValue) return;
    this.isDirty = true;
    this.saveStatus = "idle";
    this.triggerAutoSave();
  }

  // ==========================================================================
  // Pin toggle
  // ==========================================================================

  togglePin(): void {
    if (!this.note) return;
    this.note.is_pinned = !this.note.is_pinned;
    this.isDirty = true;
    this.triggerAutoSave();
    this.cdr.markForCheck();
  }

  togglePrivate(): void {
    if (!this.note) return;
    this.note.is_private = !this.note.is_private;
    this.isDirty = true;
    this.triggerAutoSave();
    this.cdr.markForCheck();
  }

  toggleUrgent(): void {
    if (!this.note) return;
    this.note.is_urgent = !this.note.is_urgent;
    this.isDirty = true;
    this.triggerAutoSave();
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Attachment search
  // ==========================================================================

  private setupAttachmentSearch(): void {
    this.attachmentSearchSubscription = this.attachmentSearchSubject
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          if (query.length < 2) {
            this.attachmentSearchResults = [];
            this.isSearchingAttachments = false;
            this.cdr.markForCheck();
            return EMPTY;
          }
          this.isSearchingAttachments = true;
          this.cdr.markForCheck();
          return this.noteService.searchAttachments(query);
        }),
      )
      .subscribe({
        next: (groups) => {
          this.attachmentSearchResults = groups || [];
          this.isSearchingAttachments = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.attachmentSearchResults = [];
          this.isSearchingAttachments = false;
          this.cdr.markForCheck();
        },
      });
  }

  onAttachmentSearch(value: string): void {
    this.attachmentSearchQuery = value;
    this.attachmentSearchSubject.next(value);
  }

  addAttachment(objectType: string, objectId: string, label: string): void {
    if (!this.note) return;

    // Verifier doublons
    const exists = this.note.attachments.some((a) => a.object_type === objectType && a.object_id === objectId);
    if (exists) return;

    this.note.attachments = [...this.note.attachments, { object_type: objectType, object_id: objectId, label }];

    // Fermer le panneau et réinitialiser la recherche
    this.showAttachmentSearch = false;
    this.attachmentSearchQuery = "";
    this.attachmentSearchResults = [];
    this.isDirty = true;
    this.triggerAutoSave();
    this.cdr.markForCheck();
  }

  toggleAttachmentSearch(): void {
    this.showAttachmentSearch = !this.showAttachmentSearch;
    if (!this.showAttachmentSearch) {
      this.attachmentSearchQuery = "";
      this.attachmentSearchResults = [];
    }
    this.cdr.markForCheck();
  }

  private loadEmployees(): void {
    this.employeesService
      .getAllEnabledEmployees()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((employees) => {
        this.employees = employees;
        this.cdr.markForCheck();
      });
  }

  removeAttachment(attachment: NoteAttachment): void {
    if (!this.note) return;

    this.note.attachments = this.note.attachments.filter(
      (a) => !(a.object_type === attachment.object_type && a.object_id === attachment.object_id),
    );
    this.isDirty = true;
    this.triggerAutoSave();
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Delete
  // ==========================================================================

  confirmDelete(): void {
    this.showDeleteDialog = true;
    this.cdr.markForCheck();
  }

  executeDelete(): void {
    this.showDeleteDialog = false;
    this.deleteNote();
  }

  private deleteNote(): void {
    if (!this.note) return;

    this.noteService
      .delete(this.note.note_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Note archivée");
          this.backToList.emit();
        },
        error: () => {
          this.evaService.showError("Erreur lors de la suppression", "deleteNote", "");
        },
      });
  }

  // ==========================================================================
  // Navigation
  // ==========================================================================

  goBack(): void {
    // Supprimer silencieusement si la note est vide (titre par défaut + pas de contenu)
    if (this.note && this.isNoteEmpty()) {
      this.isDirty = false;
      this.noteService.delete(this.note.note_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    } else {
      // Flush la sauvegarde en attente avant de quitter
      this.flushSave();
    }
    this.backToList.emit();
  }

  /** Vérifie si la note est restée vide */
  private isNoteEmpty(): boolean {
    const trimmed = (this.editorValue || "").trim();
    return !trimmed || trimmed === "Sans titre";
  }

  /** Duplique la note courante */
  private duplicateNote(): void {
    if (!this.note) return;

    this.noteService
      .duplicate(this.note.note_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.evaService.showSuccess("Note dupliquée");
          this.backToList.emit();
        },
        error: () => {
          this.evaService.showError("Impossible de dupliquer la note", "duplicateNote", "");
        },
      });
  }

  // ==========================================================================
  // Helpers
  // ==========================================================================

  /** Fusionne title + content en une valeur éditeur unique */
  private mergeToEditor(title: string, content: string): string {
    const t = (title || "").trim();
    const c = (content || "").trim();
    if (!t && !c) return "";
    if (!c) return t;
    if (!t) return c;
    return t + "\n\n" + c;
  }

  /** Extrait title (première ligne, max 200 chars) et content (le reste) */
  private splitFromEditor(value: string): { title: string; content: string } {
    const lines = (value || "").split("\n");
    const title = (lines[0] || "").trim().substring(0, 200);
    // Retire la première ligne + les lignes vides qui suivent
    let rest = lines.slice(1).join("\n");
    // Retirer les lignes vides de début du contenu
    rest = rest.replace(/^\n+/, "");
    return { title, content: rest };
  }

  /** Ouvre le rattachement dans un nouvel onglet */
  openAttachment(att: NoteAttachment): void {
    const route = OBJECT_TYPE_TO_ROUTE[att.object_type];
    if (!route) return;
    window.open(`/#/${route}/${att.object_id}`, "_blank");
  }

  /** Retourne le preset EVA_OBJECT_TAGS avec le nom de l'objet comme label (icône = type) */
  getAttachmentPreset(att: NoteAttachment): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[att.object_type] || att.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: att.object_type, severity: "secondary" as const };
    const name = att.label || att.object_id;
    return { ...base, label: name };
  }

  private formatDateShort(date: Date | string): string {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }

  private formatDateFull(date: Date | string): string {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}.${month}.${year} à ${hours}:${minutes}`;
  }
}
